import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, Lock, User, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { ShareCostLogo } from '@/components/common/ShareCostLogo';
import { Footer } from '@/components/layout/Footer';
import { Analytics } from '@/services/analytics.service';
import { useTranslation } from 'react-i18next';

export function SignUpPage() {
  const { t } = useTranslation();
  const { signUpWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError(t('web:signUpPage.passwordTooShort'));
      return;
    }

    setIsLoading(true);
    try {
      await signUpWithEmail(email, password, name);
      Analytics.track({ name: 'Sign Up Completed', properties: { method: 'email', has_display_name: !!name } });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('web:signUpPage.failedToCreateAccount'));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-secondary">
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">{t('auth:signUp.checkEmail.title')}</h2>
            <p className="mt-2 text-text-secondary">
              {t('web:signUpPage.weSentAConfirmationEmailTo')} <strong>{email}</strong>.
              {t('web:signUpPage.pleaseVerifyYourEmail')}
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
            >
              {t('web:common.backToSignIn')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-secondary">
      <div className="flex flex-1 items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <ShareCostLogo className="mx-auto mb-4 h-14 w-14 rounded-xl" />
          <h1 className="text-2xl font-bold text-text-primary">{t('web:signUpPage.createYourAccount')}</h1>
          <p className="mt-1 text-text-secondary">{t('web:signUpPage.startSplittingExpensesWithFriends')}</p>
        </div>

        <div className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <button
            onClick={() => signInWithGoogle()}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-border-secondary bg-surface-primary px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {t('web:common.continueWithGoogle')}
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-surface-inset" />
            <span className="text-xs text-text-quaternary">{t('web:common.or')}</span>
            <div className="h-px flex-1 bg-surface-inset" />
          </div>

          <form onSubmit={handleSignUp} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-text-secondary">
                {t('web:common.displayName')}
              </label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-quaternary" />
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  placeholder={t('web:signUpPage.yourName')}
                  className="w-full rounded-lg border border-border-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary">
                {t('auth:form.placeholder.email')}
              </label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-quaternary" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-border-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary">
                {t('auth:form.placeholder.password')}
              </label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-quaternary" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={t('web:signUpPage.atLeast6Characters')}
                  className="w-full rounded-lg border border-border-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {t('auth:signUp.title')}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-text-secondary">
          {t('auth:signUp.alreadyHaveAccount')}{' '}
          <Link to="/login" className="font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
            {t('auth:signIn.button')}
          </Link>
        </p>
      </div>
      </div>
      <Footer />
    </div>
  );
}
